import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { 
  FaHome, FaUsers, FaVideo, FaMoneyBillWave, FaChartLine, 
  FaCog, FaSignOutAlt, FaBars, FaTimes, FaBell, FaUserCircle, FaAngleDown,
  FaEnvelope
} from 'react-icons/fa';
import { authService, userService, UserConstants } from '../../services/ParseService';
import { useAuth } from '../../context/AuthContext';
import AgentDetails from '../../components/dashboard/AgentDetails';
import StreamsList from '../../components/dashboard/StreamsList';
import SupportChat from '../../components/dashboard/SupportChat';
import { useTranslation } from 'react-i18next';
import RoleBasedContent from '../../components/RoleBasedContent';
import AgentDashboard from '../../components/dashboard/AgentDashboard';
import HostDashboard from '../../components/dashboard/HostDashboard';
import HostsManagement from '../../components/hosts/HostsManagement';
import AgencyOptionsPage from '../agency/AgencyOptionsPage';

// Import TokLive assets
import tokLiveLogo from '../../assets/icons/ic_logo_white.png';
import tokLiveIcon from '../../assets/icons/ic_icon.png';

// Styled Components
const DashboardContainer = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

const TopBar = styled.div`
  position: fixed;
  top: 0;
  left: 280px;
  right: 0;
  height: 70px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;

  @media (max-width: 768px) {
    left: 0;
  }
`;

const Sidebar = styled.div`
  width: ${props => props.isCollapsed ? '70px' : '280px'};
  background-color: #1a2a6c;
  color: white;
  transition: all 0.3s ease;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1001;
  overflow: hidden;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-280px)'};
    width: 280px;
  }
`;

const SidebarHeader = styled.div`
  padding: ${props => props.isCollapsed ? '1rem' : '1.5rem'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: ${props => props.isCollapsed ? 'center' : 'space-between'};
  min-height: 70px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: bold;
  
  img {
    height: ${props => props.isCollapsed ? '35px' : '50px'};
    margin-right: ${props => props.isCollapsed ? '0' : '10px'};
    transition: all 0.3s ease;
  }
  
  span {
    display: ${props => props.isCollapsed ? 'none' : 'block'};
    white-space: nowrap;
  }
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 12px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.isCollapsed ? 'rotate(-90deg)' : 'rotate(90deg)'};
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SidebarMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
`;

const MenuItem = styled.li`
  margin: 8px 12px;
  border-radius: 8px;
  overflow: hidden;
  
  button {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 12px 16px;
    color: white;
    text-decoration: none;
    transition: all 0.3s ease;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1rem;
    
    &:hover, &.active {
      background-color: rgba(255, 255, 255, 0.15);
      transform: translateX(4px);
    }
    
    svg {
      min-width: 20px;
      margin-right: ${props => props.isCollapsed ? '0' : '12px'};
      font-size: 1.2rem;
    }
    
    span {
      display: ${props => props.isCollapsed ? 'none' : 'block'};
      white-space: nowrap;
    }
  }
`;

const MainContent = styled.div`
  margin-left: 280px;
  width: calc(100% - 280px);
  height: 100vh;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoutContainer = styled.div`
  margin-left: auto;
`;

const MenuToggle = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #1a2a6c;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const LogoutButton = styled.button`
  background: none;
  border: none;
  color: #1a2a6c;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: background-color 0.3s;

  &:hover {
    background-color: rgba(26, 42, 108, 0.1);
  }

  svg {
    margin-right: 0.5rem;
  }
`;

const NotificationIcon = styled.div`
  position: relative;
  margin-right: 1.5rem;
  cursor: pointer;
  
  .badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background-color: #e74c3c;
    color: white;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    font-size: 0.75rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  
  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 0.75rem;
    background-color: #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 1.5rem;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  
  .user-info {
    .name {
      font-weight: bold;
    }
    
    .role {
      font-size: 0.75rem;
      color: #666;
    }
  }
`;

const UserProfileContainer = styled.div`
  position: relative;
  cursor: pointer;
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  min-width: 200px;
  z-index: 1000;
  margin-top: 10px;
  overflow: hidden;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #f4f4f4;
  }
  
  svg {
    margin-right: 10px;
    color: #1a2a6c;
  }
`;

const ContentCard = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  
  .card-title {
    font-size: 1.2rem;
    font-weight: bold;
    margin-bottom: 1rem;
    color: #333;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    font-weight: bold;
    color: #666;
  }
  
  tbody tr:hover {
    background-color: #f9f9f9;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: bold;
  
  &.online {
    background-color: #d4edda;
    color: #155724;
  }
  
  &.offline {
    background-color: #f8d7da;
    color: #721c24;
  }
  
  &.streaming {
    background-color: #cce5ff;
    color: #004085;
  }
`;

const UnauthorizedMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

const PageTitle = styled.h1`
  margin-bottom: 2rem;
  font-size: 1.8rem;
  color: #333;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  .title {
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
  
  .value {
    font-size: 1.8rem;
    font-weight: bold;
    color: #333;
  }
  
  .change {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    
    &.positive {
      color: #27ae60;
    }
    
    &.negative {
      color: #e74c3c;
    }
  }
`;

const ContentArea = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 90px 20px 20px;
  background-color: #f4f4f4;
`;

// AsyncRoleCheck component to handle role-based rendering
const AsyncRoleCheck = ({ user }) => {
  const [hasAnyRole, setHasAnyRole] = useState(null);

  useEffect(() => {
    const checkRoles = async () => {
      try {
        // Get all user roles
        const roles = await authService.getUserRoles(user);
        setHasAnyRole(roles.length > 0);
      } catch (error) {
        console.error('Error checking roles:', error);
        setHasAnyRole(false);
      }
    };

    checkRoles();
  }, [user]);

  // Only show AgencyOptionsPage if we've confirmed the user has no roles
  if (hasAnyRole === false) {
    return <AgencyOptionsPage />;
  }

  return null;
};

const DashboardPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState('dashboard');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [userRole, setUserRole] = useState(null);

  const getRoleText = () => {
    if (userRole === UserConstants.ROLE_AGENT) return t('dashboard.role.agent');
    if (userRole === UserConstants.ROLE_ADMIN) return t('dashboard.role.admin');
    if (userRole === UserConstants.ROLE_HOST) return t('dashboard.role.host');
    return 'Agency';
  };

  // Fetch full user profile if needed
  const [userProfile, setUserProfile] = useState(null);
  
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (currentUser) {
        try {
          const profile = await userService.getUserProfile(currentUser.id);
          setUserProfile(profile);
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  // Determine user display information
  const getUserAvatar = () => {
    // Priority: 
    // 1. User profile avatar
    // 2. Current user avatar
    // 3. Default icon
    return userProfile?.avatar || 
           currentUser?.avatar || 
           null;
  };

  const getUserFullName = () => {
    // Priority:
    // 1. User profile full name
    // 2. Current user full name
    // 3. Fallback to 'User'
    return userProfile?.fullName || 
           currentUser?.fullName || 
           currentUser?.name || 
           'User';
  };

  const getUserUid = () => {
    // Priority:
    // 1. Current user get("uid")
    // 2. Fallback to 'Unknown'
    return currentUser?.get("uid") || 'Unknown';
  };

  const handleLogout = () => {
    confirmAlert({
      title: t('dashboard.logout.title'),
      message: t('dashboard.logout.confirmMessage'),
      buttons: [
        {
          label: t('dashboard.logout.yes'),
          onClick: async () => {
            try {
              await authService.logout();
              toast.success(t('dashboard.logout.success'));
              navigate('/');
            } catch (error) {
              console.error('Logout error:', error);
              toast.error(t('dashboard.logout.error'));
            }
          }
        },
        {
          label: t('dashboard.logout.no'),
          onClick: () => {}
        }
      ],
      closeOnEscape: true,
      closeOnClickOutside: true
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if user has host or agent role
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [canViewSidebar, setCanViewSidebar] = useState(false);

  useEffect(() => {
    const checkUserRoles = async () => {
      if (!currentUser) {
        setIsAuthorized(false);
        setCanViewSidebar(false);
        return;
      }

      try {
        // Get all user roles
        const userRoles = await authService.getUserRoles(currentUser);

        // Check if user has host or agent role
        setIsAuthorized(
          userRoles.some(role => [
            UserConstants.ROLE_HOST,
            UserConstants.ROLE_AGENT
          ].includes(role))
        );

        // Check if user can view sidebar
        const foundRole = userRoles.find(role => [
          UserConstants.ROLE_AGENT,
          UserConstants.ROLE_ADMIN,
          UserConstants.ROLE_HOST
        ].includes(role));
        
        setCanViewSidebar(!!foundRole);
        setUserRole(foundRole || null);
      } catch (error) {
        console.error('Error checking user roles:', error);
        setIsAuthorized(false);
        setCanViewSidebar(false);
      }
    };

    checkUserRoles();
  }, [currentUser]);

  return (
    <DashboardContainer>
      {canViewSidebar && (
        <Sidebar isOpen={sidebarOpen} isCollapsed={isCollapsed}>
          <SidebarHeader isCollapsed={isCollapsed}>
            <Logo isCollapsed={isCollapsed}>
              <img src={isCollapsed ? tokLiveIcon : tokLiveLogo} alt="TokLive Logo" />
              <span>{getRoleText()}</span>
            </Logo>
          </SidebarHeader>
          <SidebarMenu>
            <MenuItem isCollapsed={isCollapsed}>
              <button 
                type="button" 
                className={selectedMenuItem === 'dashboard' ? 'active' : ''}
                onClick={() => setSelectedMenuItem('dashboard')}
              >
                <FaHome />
                <span>{t('dashboard.menu.dashboard')}</span>
              </button>
            </MenuItem>

            {/* Menu items for Admins */}
            <RoleBasedContent roles={[UserConstants.ROLE_ADMIN]}>
              <MenuItem isCollapsed={isCollapsed}>
                <button 
                  type="button"
                  className={selectedMenuItem === 'admin' ? 'active' : ''}
                  onClick={() => setSelectedMenuItem('admin')}
                >
                  <FaUsers />
                  <span>{t('dashboard.menu.admin')}</span>
                </button>
              </MenuItem>
            </RoleBasedContent>

            {/* Menu items for Agents */}
            <RoleBasedContent roles={[UserConstants.ROLE_AGENT]}>
              <MenuItem isCollapsed={isCollapsed}>
                <button 
                  type="button"
                  className={selectedMenuItem === 'hosts' ? 'active' : ''}
                  onClick={() => setSelectedMenuItem('hosts')}
                >
                  <FaUsers />
                  <span>{t('dashboard.menu.hosts')}</span>
                </button>
              </MenuItem>
            </RoleBasedContent>

            {/* Menu items for Hosts */}
            <RoleBasedContent roles={[UserConstants.ROLE_HOST]}>
              <MenuItem isCollapsed={isCollapsed}>
                <button 
                  type="button"
                  className={selectedMenuItem === 'agent' ? 'active' : ''}
                  onClick={() => setSelectedMenuItem('agent')}
                >
                  <FaUserCircle />
                  <span>{t('dashboard.menu.agent')}</span>
                </button>
              </MenuItem>
              <MenuItem isCollapsed={isCollapsed}>
                <button 
                  type="button"
                  className={selectedMenuItem === 'streams' ? 'active' : ''}
                  onClick={() => setSelectedMenuItem('streams')}
                >
                  <FaVideo />
                  <span>{t('dashboard.menu.streams')}</span>
                </button>
              </MenuItem>
              <MenuItem isCollapsed={isCollapsed}>
                <button 
                  type="button"
                  className={selectedMenuItem === 'support' ? 'active' : ''}
                  onClick={() => setSelectedMenuItem('support')}
                >
                  <FaEnvelope />
                  <span>{t('dashboard.menu.support')}</span>
                </button>
              </MenuItem>
            </RoleBasedContent>
          </SidebarMenu>
          <CollapseButton 
            onClick={() => setIsCollapsed(!isCollapsed)} 
            isCollapsed={isCollapsed}
          >
            <FaAngleDown />
          </CollapseButton>
        </Sidebar>
      )}
      
      <MainContent style={{
        marginLeft: canViewSidebar ? (isCollapsed ? '70px' : '280px') : '0',
        width: canViewSidebar ? (isCollapsed ? 'calc(100% - 70px)' : 'calc(100% - 280px)') : '100%'
      }}>
        <TopBar style={{
          left: canViewSidebar ? (isCollapsed ? '70px' : '280px') : '0'
        }}>
          <TopBarLeft>
            {!isAuthorized && (
              <Logo>
                <img src={tokLiveLogo} alt="TokLive Logo" />
                Agency
              </Logo>
            )}
            {canViewSidebar && (
              <MenuToggle onClick={() => setSidebarOpen(!sidebarOpen)}>
                <FaBars />
              </MenuToggle>
            )}
          </TopBarLeft>
          <TopBarRight>
            <NotificationIcon>
              <FaBell />
              <span className="badge">3</span>
            </NotificationIcon>
            
            <UserProfileContainer ref={dropdownRef}>
              <UserProfile onClick={() => setDropdownOpen(!dropdownOpen)}>
                <div className="avatar">
                  {getUserAvatar() ? (
                    <img 
                      src={getUserAvatar()} 
                      alt="Profile" 
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '50%', 
                        objectFit: 'cover' 
                      }} 
                    />
                  ) : (
                    <FaUserCircle />
                  )}
                </div>
                <div className="user-info">
                  <div className="name">{getUserFullName()}</div>
                  <div className="role">{getUserUid()}</div>
                </div>
                <FaAngleDown style={{ marginLeft: '10px' }} />
              </UserProfile>

              <UserDropdown isOpen={dropdownOpen}>
                <DropdownItem onClick={() => {
                  // TODO: Implement settings page navigation
                  toast.info(t('toast.settings.comingSoon'));
                  setDropdownOpen(false);
                }}>
                  <FaCog /> {t('dashboard.settings')}
                </DropdownItem>
                <DropdownItem onClick={() => {
                  handleLogout();
                  setDropdownOpen(false);
                }}>
                  <FaSignOutAlt /> {t('dashboard.logout.title')}
                </DropdownItem>
              </UserDropdown>
            </UserProfileContainer>
          </TopBarRight>
        </TopBar>
        
        <ContentArea>
          {/* Show content based on selected menu item */}
          <RoleBasedContent roles={[UserConstants.ROLE_HOST]}>
            {selectedMenuItem === 'agent' && <AgentDetails />}
            {selectedMenuItem === 'streams' && <StreamsList />}
            {selectedMenuItem === 'support' && <SupportChat />}
          </RoleBasedContent>

          <RoleBasedContent roles={[UserConstants.ROLE_AGENT, UserConstants.ROLE_ADMIN]}>
            {selectedMenuItem === 'hosts' && <HostsManagement />}
          </RoleBasedContent>

          {selectedMenuItem === 'dashboard' && (
            <>
              {isAuthorized && <PageTitle>{t('dashboard.overview.title')}</PageTitle>}
              <RoleBasedContent roles={[UserConstants.ROLE_AGENT, UserConstants.ROLE_ADMIN]}>
                <AgentDashboard stats={{
                  activeHosts: 24,
                  totalRevenue: 8245,
                  performance: 92
                }} />
              </RoleBasedContent>

              <RoleBasedContent roles={[UserConstants.ROLE_HOST]}>
                <HostDashboard stats={{
                  totalStreams: 156,
                  totalViewers: 12450,
                  totalEarnings: 2340
                }} />
              </RoleBasedContent>
            </>
          )}

          {/* Show agency options for users without any role */}
          {currentUser && (
            <React.Suspense fallback={null}>
              <AsyncRoleCheck user={currentUser} />
            </React.Suspense>
          )}
        </ContentArea>
      </MainContent>
    </DashboardContainer>
  );
};

export default DashboardPage;
